import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Wifi, WifiOff, RefreshCw, Link2, Trash2, Loader2, X, Send, CheckCircle2, AlertTriangle, Settings, Bot } from 'lucide-react';
import { PlatformIcon } from '../../components/PlatformIcon';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';

const TYPES = ['Telegram', 'Email', 'WhatsApp', 'SMS', 'Discord'];

export default function ConnectionsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({ name: '', type: 'Telegram', token: '', config: '' });
    const [saving, setSaving] = useState(false);
    // Telegram Bot Config
    const [botConfig, setBotConfig] = useState(null);
    const [botForm, setBotForm] = useState({ token: '', username: '' });
    const [botSaving, setBotSaving] = useState(false);
    const [botTesting, setBotTesting] = useState(false);
    const [showBotConfig, setShowBotConfig] = useState(true);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchConnections = useCallback(async () => {
        try {
            const res = await fetch(`${API}/connections`, { headers });
            if (res.ok) setConnections(await res.json());
        } catch {} finally { setLoading(false); }
    }, []);

    const fetchBotConfig = useCallback(async () => {
        try {
            const res = await fetch(`${API}/bot-config`, { headers });
            if (res.ok) {
                const data = await res.json();
                setBotConfig(data);
                if (data.token_masked) setBotForm({ token: '', username: data.username || '' });
            }
        } catch {}
    }, []);

    useEffect(() => { fetchConnections(); fetchBotConfig(); }, [fetchConnections, fetchBotConfig]);

    useEffect(() => {
        if (searchParams.get('action') === 'novo_canal') { setShowModal(true); setSearchParams({}); }
    }, [searchParams]);

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Nome obrigatorio'); return; }
        setSaving(true);
        try {
            const res = await fetch(`${API}/connections`, { method: 'POST', headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success('Canal criado'); setShowModal(false); setForm({ name: '', type: 'Telegram', token: '', config: '' }); fetchConnections(); }
            else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const toggleStatus = async (id) => {
        const res = await fetch(`${API}/connections/${id}/status`, { method: 'PUT', headers });
        if (res.ok) { const d = await res.json(); setConnections(p => p.map(c => c.connection_id === id ? { ...c, status: d.status } : c)); toast.success(`Status: ${d.status}`); }
    };

    const deleteConn = async (id) => {
        if (!window.confirm('Excluir canal?')) return;
        const res = await fetch(`${API}/connections/${id}`, { method: 'DELETE', headers });
        if (res.ok) { setConnections(p => p.filter(c => c.connection_id !== id)); toast.success('Excluido'); }
    };

    const saveBotConfig = async () => {
        const tkn = botForm.token.trim();
        if (!tkn && !botConfig?.token_masked) { toast.error('Token obrigatorio'); return; }
        if (!tkn) { toast.info('Nenhum novo token informado'); return; }
        setBotSaving(true);
        try {
            const res = await fetch(`${API}/bot-config`, { method: 'POST', headers, body: JSON.stringify({ token: tkn, username: botForm.username }) });
            if (res.ok) { const d = await res.json(); setBotConfig(d); setBotForm({ token: '', username: d.username || '' }); toast.success('Bot configurado com sucesso!'); }
            else { const e = await res.json(); toast.error(e.detail || 'Erro ao salvar'); }
        } catch { toast.error('Erro de conexão'); } finally { setBotSaving(false); }
    };

    const testBotConfig = async () => {
        setBotTesting(true);
        try {
            const res = await fetch(`${API}/bot-config/test`, { method: 'POST', headers });
            if (res.ok) { const d = await res.json(); setBotConfig(prev => ({ ...prev, status: d.status, bot_info: d.bot_info || prev?.bot_info })); toast.success(d.status === 'connected' ? 'Bot conectado!' : 'Erro: ' + (d.detail || 'falha')); }
            else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); } finally { setBotTesting(false); }
    };

    const online = connections.filter(c => c.status === 'online').length;

    return (
        <div className="sv-page" data-testid="connections-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Conexões</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>{online}/{connections.length} canais online</p>
                </div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => setShowModal(true)} data-testid="new-connection-btn"><Plus size={14} /> Novo Canal</button>
            </div>

            {/* Telegram Bot Config Section */}
            <div className="sv-card-system" data-testid="telegram-bot-config">
                <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setShowBotConfig(!showBotConfig)}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', border: '1px solid var(--border)' }}>
                            <Bot size={20} style={{ color: 'var(--sv-gold-primary)' }} />
                        </div>
                        <div>
                            <span className="sv-card-label">Telegram Bot</span>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                                {botConfig?.status === 'connected' ? `@${botConfig.username || botConfig.bot_info?.username || 'bot'}` : 'Não configurado'}
                            </p>
                        </div>
                    </div>
                    <span className={`sv-badge ${botConfig?.status === 'connected' ? 'sv-badge-success' : 'sv-badge-warning'}`}>
                        {botConfig?.status === 'connected' ? <><CheckCircle2 size={10} /> Conectado</> : <><AlertTriangle size={10} /> Pendente</>}
                    </span>
                </div>

                {showBotConfig && (
                    <div className="flex flex-col gap-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                        {/* Bot Info */}
                        {botConfig?.bot_info && (
                            <div className="p-3 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><span style={{ color: 'var(--muted-2)' }}>Nome:</span> <span className="font-medium" style={{ color: 'var(--text)' }}>{botConfig.bot_info.first_name}</span></div>
                                    <div><span style={{ color: 'var(--muted-2)' }}>Username:</span> <span className="font-medium" style={{ color: 'var(--text)' }}>@{botConfig.bot_info.username}</span></div>
                                    <div><span style={{ color: 'var(--muted-2)' }}>ID:</span> <span className="font-medium" style={{ color: 'var(--text)' }}>{botConfig.bot_info.id}</span></div>
                                    <div><span style={{ color: 'var(--muted-2)' }}>Token:</span> <span className="font-medium" style={{ color: 'var(--text)' }}>{botConfig.token_masked}</span></div>
                                </div>
                            </div>
                        )}
                        {/* Token Input */}
                        <div>
                            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>
                                {botConfig?.token_masked ? 'Novo Token (deixe vazio para manter atual)' : 'Token do Bot (@BotFather)'}
                            </label>
                            <input type="password" value={botForm.token} onChange={e => setBotForm({ ...botForm, token: e.target.value })}
                                placeholder={botConfig?.token_masked || '123456:ABC-DEF...'}
                                className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                data-testid="bot-token-input" />
                        </div>
                        <div>
                            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Username do Bot</label>
                            <input type="text" value={botForm.username} onChange={e => setBotForm({ ...botForm, username: e.target.value })}
                                placeholder="@SuperVitrineOfertasBot"
                                className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                data-testid="bot-username-input" />
                        </div>
                        <div className="flex gap-2">
                            <button className="sv-btn-system flex-1" onClick={testBotConfig} disabled={botTesting} data-testid="bot-test-btn">
                                {botTesting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Testar Conexao
                            </button>
                            <button className="sv-btn-gold-system sv-btn-system flex-1" onClick={saveBotConfig} disabled={botSaving} data-testid="bot-save-btn">
                                {botSaving ? <Loader2 size={14} className="animate-spin" /> : null} Salvar
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* KPIs */}
            <div className="sv-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="sv-kpi"><span className="sv-kpi-label">Canais Ativos</span><span className="sv-kpi-value">{online}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Total Mensagens</span><span className="sv-kpi-value">{connections.reduce((a, c) => a + (c.messages || 0), 0).toLocaleString('pt-BR')}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Canais Offline</span><span className="sv-kpi-value">{connections.length - online}</span></div>
            </div>

            {/* Connection Cards */}
            {loading ? (
                <div className="sv-card-system sv-empty-state"><Loader2 className="animate-spin" /><p className="sv-empty-state-title">Carregando...</p></div>
            ) : connections.length === 0 ? (
                <div className="sv-card-system sv-empty-state">
                    <Link2 /><p className="sv-empty-state-title">Nenhum canal adicional</p><p className="sv-empty-state-desc">Configure o bot acima ou adicione canais</p>
                </div>
            ) : (
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                    {connections.map(conn => (
                        <div key={conn.connection_id} className="sv-card-system" data-testid={`connection-${conn.connection_id}`}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                        <PlatformIcon name={conn.type} size={18} />
                                    </div>
                                    <div><p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{conn.name}</p><p className="text-xs" style={{ color: 'var(--muted-2)' }}>{conn.type}</p></div>
                                </div>
                                <span className={`sv-badge ${conn.status === 'online' ? 'sv-badge-success' : 'sv-badge-danger'}`}>
                                    {conn.status === 'online' ? <><Wifi size={10} /> Online</> : <><WifiOff size={10} /> Offline</>}
                                </span>
                            </div>
                            <div className="flex gap-2 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                                <button className="sv-btn-system flex-1" style={{ padding: '6px' }} onClick={() => toggleStatus(conn.connection_id)}>
                                    {conn.status === 'online' ? <><WifiOff size={12} /> Desconectar</> : <><RefreshCw size={12} /> Conectar</>}
                                </button>
                                <button className="sv-btn-system" style={{ padding: '6px 8px', color: '#ef4444' }} onClick={() => deleteConn(conn.connection_id)}><Trash2 size={12} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* New Connection Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="sv-card-system w-full max-w-lg" data-testid="connection-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Novo Canal</h3>
                            <button className="sv-btn-system" style={{ padding: 4 }} onClick={() => setShowModal(false)}><X size={14} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Nome</label>
                                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Bot Principal"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="connection-name-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Tipo</label>
                                <div className="flex gap-2 flex-wrap">
                                    {TYPES.map(t => (<button key={t} className={`sv-subnav-chip ${form.type === t ? 'active' : ''}`} onClick={() => setForm({ ...form, type: t })}>{t}</button>))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Token / API Key</label>
                                <input type="text" value={form.token} onChange={e => setForm({ ...form, token: e.target.value })} placeholder="Token..."
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="sv-btn-system" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="connection-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Criar Canal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
